import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Ban, ChevronLeft, ChevronRight, History, Loader2, Pencil, PiggyBank, Plus, Trash2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { CompanyTabs } from "@/components/shared/CompanyTabs";
import { BORDER, FormDrawer } from "@/components/shared/FormDrawer";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import {
  useContributionAction,
  useContributionList,
  useContributionOptions,
  useContributionSummary,
  type ContributionOptions,
  type ContributionSummary,
  type FinanceContribution,
} from "../hooks/useFinanceContributions";

const CURRENCIES = ["AED", "INR", "USD", "EUR", "GBP"];
const ICON_BUTTON =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground";
const today = () => new Date().toISOString().slice(0, 10);
const cents = (v: string | number) => Math.round(Number(v || 0) * 100);

function monthLabel(month: string, withYear = false): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", {
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

type Company = ContributionOptions["companies"][number];
type MonthRow = { period: string; amount: string };

/**
 * Optional: which months a contribution is for, e.g. 6,000 paid on 30 Sep as
 * July 1,000, August 2,500, September 2,500. Any part not given to a month
 * counts in the month received.
 */
function MonthsEditor({
  rows,
  setRows,
  credited,
  currency,
  receivedDate,
}: {
  rows: MonthRow[];
  setRows: (rows: MonthRow[]) => void;
  credited: number;
  currency: string;
  receivedDate: string;
}) {
  const allocatedCents = rows.reduce((s, r) => s + cents(r.amount), 0);
  const leftCents = cents(credited) - allocatedCents;
  const received = receivedDate.slice(0, 7);

  function addMonth() {
    // Suggest the month after the last one chosen, or the month received.
    let next = received;
    if (rows.length) {
      const [y, m] = rows[rows.length - 1].period.split("-").map(Number);
      next = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7);
    }
    setRows([...rows, { period: next, amount: leftCents > 0 ? (leftCents / 100).toFixed(2) : "" }]);
  }

  return (
    <div className="space-y-2 rounded-lg border p-3" style={BORDER}>
      <div className="flex items-center justify-between">
        <Label>For which months?</Label>
        <button type="button" onClick={addMonth} className="text-xs font-medium text-primary hover:underline">
          + Add a month
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Optional. Without months, the whole amount counts for the month it was received
          {received ? ` (${monthLabel(received, true)})` : ""}.
        </p>
      ) : (
        <>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_28px] items-center gap-2">
              <Input
                type="month"
                value={r.period}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, period: e.target.value } : x)))}
                required
              />
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={r.amount}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                placeholder={currency}
                required
              />
              <button
                type="button"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}
                className={ICON_BUTTON}
                title="Remove month"
              >
                <Trash2 className="h-[14px] w-[14px]" strokeWidth={1.5} />
              </button>
            </div>
          ))}
          <p className={`text-xs ${leftCents < 0 ? "text-destructive" : "text-muted-foreground"}`}>
            {formatCurrency(allocatedCents / 100, currency)} of {formatCurrency(credited || 0, currency)} given to months
            {leftCents > 0 &&
              ` · ${formatCurrency(leftCents / 100, currency)} counts for ${received ? monthLabel(received, true) : "the month received"}`}
            {leftCents < 0 && " · more than was received"}
          </p>
        </>
      )}
    </div>
  );
}

function NewContributionDrawer({ company, onClose }: { company: Company; onClose: () => void }) {
  const action = useContributionAction();
  const [receivedDate, setReceivedDate] = useState(today());
  const [partnerId, setPartnerId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [currency, setCurrency] = useState(company.currency);
  const [amount, setAmount] = useState("");
  const [creditedAmount, setCreditedAmount] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const converted = currency !== company.currency;
  const credited = converted ? Number(creditedAmount) : Number(amount);
  const rate = converted && Number(amount) > 0 && Number(creditedAmount) > 0 ? Number(creditedAmount) / Number(amount) : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({
        method: "POST",
        path: "",
        body: {
          receivedDate,
          partnerId,
          accountId,
          amount: Number(amount),
          currency,
          creditedAmount: converted ? Number(creditedAmount) : null,
          reference: reference || null,
          notes: notes || null,
          allocations: months.map((m) => ({ period: m.period, amount: Number(m.amount) })),
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`New Contribution: ${company.name}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <div className="space-y-2">
        <Label htmlFor="ct-partner">
          Partner <span className="text-destructive">*</span>
        </Label>
        <Select id="ct-partner" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} required>
          <option value="">Select a partner...</option>
          {company.partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.shortCode})
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ct-date">
          Date received <span className="text-destructive">*</span>
        </Label>
        <Input id="ct-date" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ct-account">
          Received into <span className="text-destructive">*</span>
        </Label>
        <Select id="ct-account" value={accountId} onChange={(e) => setAccountId(e.target.value)} required>
          <option value="">Select an account...</option>
          {company.accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Any account of {company.name}, including another partner's holding account.
        </p>
      </div>
      <div className="grid grid-cols-[110px_1fr] gap-3">
        <div className="space-y-2">
          <Label htmlFor="ct-currency">Paid in</Label>
          <Select id="ct-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {[...new Set([company.currency, ...CURRENCIES])].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ct-amount">
            Amount paid ({currency}) <span className="text-destructive">*</span>
          </Label>
          <Input id="ct-amount" type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </div>
      </div>
      {converted && (
        <div className="space-y-2">
          <Label htmlFor="ct-credited">
            Credited to account ({company.currency}) <span className="text-destructive">*</span>
          </Label>
          <Input
            id="ct-credited"
            type="number"
            min="0.01"
            step="0.01"
            value={creditedAmount}
            onChange={(e) => setCreditedAmount(e.target.value)}
            required
          />
          <p className="text-xs text-muted-foreground">
            Enter exactly what the bank statement shows. This amount counts as capital.
            {rate ? ` Rate achieved: 1 ${currency} = ${rate.toFixed(4)} ${company.currency}.` : ""}
          </p>
        </div>
      )}
      <MonthsEditor
        rows={months}
        setRows={setMonths}
        credited={credited}
        currency={company.currency}
        receivedDate={receivedDate}
      />
      <div className="space-y-2">
        <Label htmlFor="ct-ref">Reference</Label>
        <Input id="ct-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. bank reference" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ct-notes">Notes</Label>
        <Input
          id="ct-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. operating capital for October"
        />
      </div>
    </FormDrawer>
  );
}

function EditContributionDrawer({ contribution, onClose }: { contribution: FinanceContribution; onClose: () => void }) {
  const action = useContributionAction();
  const [reference, setReference] = useState(contribution.reference ?? "");
  const [notes, setNotes] = useState(contribution.notes ?? "");
  const [months, setMonths] = useState<MonthRow[]>(contribution.allocations);
  const [error, setError] = useState<string | null>(null);
  const cancelled = contribution.status === "CANCELLED";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({
        method: "PUT",
        path: `/${contribution.id}`,
        body: {
          reference,
          notes,
          ...(cancelled ? {} : { allocations: months.map((m) => ({ period: m.period, amount: Number(m.amount) })) }),
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`Edit Contribution: ${contribution.code}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <p className="text-xs text-muted-foreground">
        {contribution.partner.name} paid {formatCurrency(Number(contribution.creditedAmount), contribution.creditedCurrency)} on{" "}
        {formatDate(contribution.receivedDate)}. The date, partner, account and amounts cannot be changed; if they are
        wrong, cancel this contribution and enter it again.
      </p>
      {!cancelled && (
        <MonthsEditor
          rows={months}
          setRows={setMonths}
          credited={Number(contribution.creditedAmount)}
          currency={contribution.creditedCurrency}
          receivedDate={contribution.receivedDate}
        />
      )}
      <div className="space-y-2">
        <Label htmlFor="ce-ref">Reference</Label>
        <Input id="ce-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ce-notes">Notes</Label>
        <Input id="ce-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </FormDrawer>
  );
}

function CancelContributionDrawer({ contribution, onClose }: { contribution: FinanceContribution; onClose: () => void }) {
  const action = useContributionAction();
  const [entryDate, setEntryDate] = useState(today());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await action.mutateAsync({ method: "POST", path: `/${contribution.id}/cancel`, body: { entryDate, reason } });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={`Cancel Contribution: ${contribution.code}`} onClose={onClose} onSubmit={handleSubmit} error={error} busy={action.isPending}>
      <p className="text-xs text-muted-foreground">
        Takes {formatCurrency(Number(contribution.creditedAmount), contribution.creditedCurrency)} back out of{" "}
        {contribution.account.name} with a reversing entry. The contribution stays on record as cancelled.
      </p>
      <div className="space-y-2">
        <Label htmlFor="cc-date">
          Reversal date <span className="text-destructive">*</span>
        </Label>
        <Input id="cc-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cc-reason">
          Reason <span className="text-destructive">*</span>
        </Label>
        <Input
          id="cc-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Entered against the wrong partner"
          required
        />
      </div>
    </FormDrawer>
  );
}

function CapitalCards({ summary }: { summary: ContributionSummary }) {
  const currency = summary.company.currency;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {summary.rows.map((r) => (
        <div key={r.partner.id} className="rounded-xl border bg-white p-4" style={BORDER}>
          <div className="text-xs text-muted-foreground">
            {r.partner.name} ({r.partner.shortCode})
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">{formatCurrency(Number(r.contributed), currency)}</div>
          <div className="text-xs text-muted-foreground">capital contributed</div>
        </div>
      ))}
    </div>
  );
}

/** What each partner contributed in each month of a year, by date received. */
function MonthGrid({
  summary,
  year,
  setYear,
}: {
  summary: ContributionSummary | undefined;
  year: number;
  setYear: (year: number) => void;
}) {
  const fmt = (v: string | number) =>
    Number(v) ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—";
  const current = new Date().toISOString().slice(0, 7);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button type="button" className={ICON_BUTTON} onClick={() => setYear(year - 1)} title="Previous year">
          <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <span className="text-sm font-medium tabular-nums">{year}</span>
        <button type="button" className={ICON_BUTTON} onClick={() => setYear(year + 1)} title="Next year">
          <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
        {summary && (
          <span className="text-xs text-muted-foreground">
            Contributed for each month, in {summary.company.currency}: the months chosen, otherwise the month received.
          </span>
        )}
      </div>
      {!summary ? (
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white" style={BORDER}>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground" style={BORDER}>
                <th className="sticky left-0 bg-white px-3 py-2 font-medium">Partner</th>
                {summary.months.map((m) => (
                  <th key={m} className={`px-2 py-2 text-right font-medium ${m === current ? "text-foreground" : ""}`}>
                    {monthLabel(m)}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">{year}</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-4 py-6 text-center text-muted-foreground">
                    No partners hold shares in {summary.company.name}. Set ownership under Business Structure → Partners.
                  </td>
                </tr>
              )}
              {summary.rows.map((r) => (
                <tr key={r.partner.id} className="border-b last:border-0" style={BORDER}>
                  <td className="sticky left-0 bg-white px-3 py-2 font-medium">{r.partner.shortCode}</td>
                  {r.months.map((c) => (
                    <td key={c.month} className="px-2 py-2 text-right tabular-nums">
                      {fmt(c.amount)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{fmt(r.yearTotal)}</td>
                </tr>
              ))}
              {summary.rows.length > 1 && (
                <tr className="border-t bg-[hsl(210,40%,98%)] font-medium" style={BORDER}>
                  <td className="sticky left-0 bg-[hsl(210,40%,98%)] px-3 py-2">Total</td>
                  {summary.monthTotals.map((c) => (
                    <td key={c.month} className="px-2 py-2 text-right tabular-nums">
                      {fmt(c.amount)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmt(summary.rows.reduce((t, r) => t + cents(r.yearTotal), 0) / 100)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ContributionsPage() {
  const { user } = useAuth();
  const permissions = user?.permissions?.finance_contributions;
  const { data: options, isLoading: optionsLoading } = useContributionOptions();
  const [companyId, setCompanyId] = useState("");
  const [tab, setTab] = useState<"list" | "months">("list");
  const [year, setYear] = useState(() => new Date().getUTCFullYear());
  const [drawer, setDrawer] = useState<
    | { kind: "new" }
    | { kind: "edit"; contribution: FinanceContribution }
    | { kind: "cancel"; contribution: FinanceContribution }
    | null
  >(null);
  const [activity, setActivity] = useState<FinanceContribution | null>(null);

  useEffect(() => {
    if (!companyId && options?.companies.length) setCompanyId(options.companies[0].id);
  }, [companyId, options]);

  const company = options?.companies.find((c) => c.id === companyId);
  const { data: summary } = useContributionSummary(companyId, year);
  // Loaded for every company, so each tab can show its count.
  const { data: allContributions = [], isLoading } = useContributionList({});
  const contributions = useMemo(
    () => allContributions.filter((c) => c.companyId === companyId),
    [allContributions, companyId],
  );
  const tabs = (options?.companies ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    count: allContributions.filter((x) => x.companyId === c.id).length,
  }));

  const received = useMemo(
    () => contributions.filter((c) => c.status === "RECEIVED").reduce((s, c) => s + cents(c.creditedAmount), 0),
    [contributions],
  );

  return (
    <AppLayout breadcrumb={[{ label: "PhazeX Hub", href: "/" }, { label: "Finance" }, { label: "Contributions" }]}>
      <div className="w-full space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <PiggyBank className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Capital Contributions</h1>
          </div>
          <div className="flex items-center gap-2">
            {permissions?.canExport && company && (
              <ExportButton module="finance-contributions" filters={{ companyId }} />
            )}
            {permissions?.canCreate && company && (
              <Button onClick={() => setDrawer({ kind: "new" })}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                New Contribution
              </Button>
            )}
          </div>
        </div>

        {tabs.length > 0 && <CompanyTabs companies={tabs} value={companyId} onChange={setCompanyId} />}

        {optionsLoading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />}
        {!optionsLoading && !company && (
          <div className="rounded-xl border bg-white p-6 text-sm text-muted-foreground" style={BORDER}>
            No operational companies found.
          </div>
        )}

        {company && summary && <CapitalCards summary={summary} />}

        {company && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-lg border bg-white p-0.5" style={BORDER}>
                {(
                  [
                    ["list", "Contributions"],
                    ["months", "By month"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`rounded-md px-3 py-1.5 text-sm ${
                      tab === key ? "bg-[hsl(210,50%,96%)] font-medium text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                Total contributed: {formatCurrency(received / 100, company.currency)}
              </span>
            </div>

            {tab === "months" && <MonthGrid summary={summary} year={year} setYear={setYear} />}

            {tab === "list" && (
              <div className="overflow-hidden rounded-xl border bg-white" style={BORDER}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground" style={BORDER}>
                      <th className="px-4 py-3 font-medium">Received</th>
                      <th className="px-4 py-3 font-medium">#</th>
                      <th className="px-4 py-3 font-medium">Partner</th>
                      <th className="px-4 py-3 font-medium">Into</th>
                      <th className="px-4 py-3 text-right font-medium">Amount</th>
                      <th className="px-4 py-3 font-medium">For</th>
                      <th className="px-4 py-3 font-medium">Reference</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
                        </td>
                      </tr>
                    )}
                    {!isLoading && contributions.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                          No contributions recorded for {company.name} yet
                        </td>
                      </tr>
                    )}
                    {contributions.map((c) => {
                      const cancelled = c.status === "CANCELLED";
                      return (
                        <tr key={c.id} className="border-b align-top last:border-0" style={BORDER}>
                          <td className="whitespace-nowrap px-4 py-3">{formatDate(c.receivedDate)}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.code}</td>
                          <td className="px-4 py-3">
                            <div className={cancelled ? "text-muted-foreground line-through" : "font-medium"}>{c.partner.name}</div>
                            {c.notes && <div className="text-xs text-muted-foreground">{c.notes}</div>}
                            {c.sourceType && <div className="text-xs text-muted-foreground">Created by a transfer</div>}
                            {cancelled && <div className="text-xs text-destructive">Cancelled: {c.cancelReason}</div>}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            <Link to={`/finance/accounts/${c.accountId}`} className="hover:underline">
                              {c.account.name}
                            </Link>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                            {formatCurrency(Number(c.creditedAmount), c.creditedCurrency)}
                            {c.rate && (
                              <div className="text-xs text-muted-foreground">
                                {formatCurrency(Number(c.amount), c.currency)} @ {Number(c.rate).toFixed(4)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {c.allocations.length === 0 ? (
                              <span className="text-muted-foreground">{monthLabel(c.receivedDate.slice(0, 7), true)}</span>
                            ) : (
                              <>
                                {c.allocations.map((a) => (
                                  <div key={a.period} className="whitespace-nowrap tabular-nums">
                                    {monthLabel(a.period, true)}: {Number(a.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                  </div>
                                ))}
                                {Number(c.unallocatedAmount) > 0 && (
                                  <div className="whitespace-nowrap tabular-nums text-muted-foreground">
                                    {monthLabel(c.receivedDate.slice(0, 7), true)}:{" "}
                                    {Number(c.unallocatedAmount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{c.reference ?? "—"}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            <button type="button" onClick={() => setActivity(c)} className={ICON_BUTTON} title="Activity history">
                              <History className="h-[14px] w-[14px]" strokeWidth={1.5} />
                            </button>
                            {permissions?.canEdit && (
                              <button
                                type="button"
                                onClick={() => setDrawer({ kind: "edit", contribution: c })}
                                className={ICON_BUTTON}
                                title="Edit months and details"
                              >
                                <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                              </button>
                            )}
                            {permissions?.canDelete && !cancelled && !c.sourceType && (
                              <button
                                type="button"
                                onClick={() => setDrawer({ kind: "cancel", contribution: c })}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                                title="Cancel contribution"
                              >
                                <Ban className="h-[14px] w-[14px]" strokeWidth={1.5} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {company && drawer?.kind === "new" && <NewContributionDrawer company={company} onClose={() => setDrawer(null)} />}
      {drawer?.kind === "edit" && <EditContributionDrawer contribution={drawer.contribution} onClose={() => setDrawer(null)} />}
      {drawer?.kind === "cancel" && <CancelContributionDrawer contribution={drawer.contribution} onClose={() => setDrawer(null)} />}
      {activity && (
        <ActivityHistoryDrawer
          isOpen
          onClose={() => setActivity(null)}
          entityType="finance_contribution"
          entityId={activity.id}
          entityLabel={activity.code}
        />
      )}
    </AppLayout>
  );
}
